import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils/cn";

/**
 * A single-select that can be searched — a native `<select>` stops being usable
 * once the list outgrows a screenful, which partner groups will.
 *
 * Mirrors the reports `MultiSelectFilter`: same trigger, same panel, same
 * "search only once the list is long" rule, so the two read as one control
 * family rather than two takes on a dropdown.
 */

/** Below this, a search box is one more thing to tab past for a visible list. */
const SEARCH_THRESHOLD = 8;

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  id,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { id: string; name: string }[];
  /** Shown when nothing is selected. Picking it again clears the selection. */
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  id?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const selected = options.find((o) => o.id === value);

  // Opening with a stale query would show a filtered list the user did not ask
  // for; clear it and put the caret where they are about to type.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    searchRef.current?.focus();
  }, [open]);

  // While open, Escape closes the list and nothing else: listening on the
  // window in the capture phase runs ahead of the Modal or Sheet around it,
  // which listen on the document.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div className={cn("relative w-full", className)}>
      <button
        type="button"
        id={id}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex min-h-touch w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className={cn("truncate", !selected && "text-muted-foreground")}>
          {selected ? selected.name : placeholder}
        </span>
        <ChevronDown className="ml-auto size-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-20 mt-1 w-full min-w-64 rounded-lg border border-border bg-popover p-2 shadow-lg">
            {options.length > SEARCH_THRESHOLD && (
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="mb-2 w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
              />
            )}
            <div className="max-h-60 space-y-0.5 overflow-y-auto pr-1" role="listbox">
              <Option
                label={placeholder}
                selected={!value}
                muted
                onSelect={() => choose("")}
              />
              {filtered.length === 0 ? (
                <p className="px-1 py-2 text-sm text-muted-foreground">{emptyLabel}</p>
              ) : (
                filtered.map((option) => (
                  <Option
                    key={option.id}
                    label={option.name}
                    selected={option.id === value}
                    onSelect={() => choose(option.id)}
                  />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Option({
  label,
  selected,
  muted,
  onSelect,
}: {
  label: string;
  selected: boolean;
  muted?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-2 rounded px-1 py-1 text-left text-sm hover:bg-accent",
        muted && "text-muted-foreground",
      )}
    >
      <Check className={cn("size-4 shrink-0", !selected && "opacity-0")} aria-hidden />
      <span className="min-w-0 flex-1">{label}</span>
    </button>
  );
}
